import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ROLES, canAssignRole, type Role } from "@rentorng/core/permissions";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Badge, Button, Card, CardTitle, ErrorState, Field, Input, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { PasswordInput } from "@/components/password-input";

/**
 * The team.
 *
 * Staff get their own logins because the audit log is worthless otherwise — which is exactly why
 * the pricing does not meter seats. An invitation can either be handed over as a link or set up
 * with a password there and then, because in a small office that is faster than waiting for an
 * email to arrive.
 */

interface MemberSuspension {
  level: "soft" | "hard";
  note: string | null;
  until: string | null;
  since: string | null;
}

interface TeamResponse {
  staff: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    portfolio: string[] | null;
    lastSeenAt: string | null;
    /** Resolved by the server: an expiry that has passed is not a suspension. */
    suspension: MemberSuspension | null;
  }>;
  pending: Array<{ id: string; email: string; role: string; expiresAt: string }>;
  myRole: Role;
}

export function TeamPage() {
  const { user, can } = useSession();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  /** The member whose restriction form is open. One at a time — this is not a bulk action. */
  const [restricting, setRestricting] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["team"],
    queryFn: () => api.get<TeamResponse>("/settings/team"),
  });

  const invite = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ token?: string; added?: boolean }>("/settings/team/invite", input),
    invalidates: [["team"]],
    onSuccess: (result) => setInviteToken(result.token ?? null),
  });

  const changeRole = useApiMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api.patch(`/settings/team/${userId}`, { role }),
    invalidates: [["team"]],
  });

  const remove = useApiMutation({
    mutationFn: (userId: string) => api.delete(`/settings/team/${userId}`),
    invalidates: [["team"]],
  });

  const suspend = useApiMutation({
    mutationFn: ({ userId, ...body }: { userId: string; level: string; note?: string; until?: string }) =>
      api.post(`/settings/team/${userId}/suspension`, body),
    invalidates: [["team"]],
    onSuccess: () => setRestricting(null),
  });

  const restore = useApiMutation({
    mutationFn: (userId: string) => api.delete(`/settings/team/${userId}/suspension`),
    invalidates: [["team"]],
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Team"
        back={{ to: "/app/settings", label: "Settings" }}
        subtitle="Staff logins are not metered. Everyone gets their own, so the audit log means something."
        description={"Everyone who can sign in, and what each of them is allowed to do. Staff logins are never metered — give everybody their own so the audit log means something."}
      />

      {invite.errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{invite.errorMessage}</Alert>
        </div>
      ) : null}

      {inviteToken ? (
        <div className="mb-4">
          <Alert tone="success" title="Invitation created.">
            Send them this link:{" "}
            <span className="break-all font-mono text-xs">{`${window.location.origin}/sign-up?invite=${inviteToken}`}</span>
          </Alert>
        </div>
      ) : null}

      {remove.errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{remove.errorMessage}</Alert>
        </div>
      ) : null}

      <Card padded={false} className="mb-5">
        <Table
          head={
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Access</th>
              <th>State</th>
              <th></th>
            </tr>
          }
        >
          {data.staff.map((member) => (
            <tr key={member.id}>
              <td>
                {member.name}
                {member.id === user?.id ? <span className="ml-2 text-xs text-ink-400">you</span> : null}
              </td>
              <td className="text-ink-500">{member.email}</td>
              <td>
                {can("org.members") && member.id !== user?.id && member.role !== "owner" ? (
                  <Select
                    aria-label={`Role for ${member.name}`}
                    value={member.role}
                    onChange={(event) => changeRole.mutate({ userId: member.id, role: event.target.value })}
                    className="w-40 text-sm"
                  >
                    {ROLES.filter((role) => canAssignRole(data.myRole, role.value)).map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Badge tone={member.role === "owner" ? "brand" : "muted"}>{member.role}</Badge>
                )}
              </td>
              <td className="text-xs text-ink-500">
                {member.role === "agent" && member.portfolio?.length
                  ? `${member.portfolio.length} properties`
                  : member.role === "agent"
                    ? "All properties"
                    : "Whole agency"}
              </td>
              <td className="text-xs">
                {/* Named for what it does. "Suspended" on its own is what makes somebody ring up
                    to ask which kind of suspended. */}
                {member.suspension ? (
                  <Badge tone={member.suspension.level === "hard" ? "danger" : "warn"}>
                    {member.suspension.level === "hard" ? "Locked out" : "Read-only"}
                  </Badge>
                ) : (
                  <span className="text-ink-400">Active</span>
                )}
                {member.suspension?.until ? (
                  <span className="mt-0.5 block text-ink-400">until {member.suspension.until.slice(0, 10)}</span>
                ) : null}
              </td>
              <td>
                {can("org.members") && member.id !== user?.id && member.role !== "owner" ? (
                  <div className="flex flex-wrap justify-end gap-1">
                    {member.suspension ? (
                      <Button variant="ghost" size="sm" onClick={() => restore.mutate(member.id)}>
                        Restore
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRestricting(restricting === member.id ? null : member.id)}
                      >
                        Restrict
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`Remove ${member.name} from this agency?`)) remove.mutate(member.id);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      </Card>

      {restricting ? (
        <Card className="mb-5">
          <CardTitle hint="Nothing is deleted either way. Restoring puts everything back exactly as it was.">
            Restrict {data.staff.find((member) => member.id === restricting)?.name}
          </CardTitle>

          {suspend.errorMessage ? (
            <div className="mb-4">
              <Alert tone="danger">{suspend.errorMessage}</Alert>
            </div>
          ) : null}

          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              suspend.mutate({
                userId: restricting,
                level: String(form.get("level")),
                note: String(form.get("note") || "") || undefined,
                until: String(form.get("until") || "") || undefined,
              });
            }}
          >
            <Field
              label="How much"
              htmlFor="level"
              hint="Read-only lets them sign in and see everything. Locked out ends their sessions and refuses a sign-in."
            >
              <Select id="level" name="level" defaultValue="soft">
                <option value="soft">Read-only — can see, cannot change</option>
                <option value="hard">Locked out — cannot sign in at all</option>
              </Select>
            </Field>
            <Field label="Until" htmlFor="until" hint="Optional. It lifts on its own at the end of this day.">
              <Input id="until" name="until" type="date" />
            </Field>
            <Field label="What they are told" htmlFor="note" hint="Optional. Shown to them, so say what to do rather than why.">
              <Input id="note" name="note" maxLength={400} placeholder="Speak to Ada before signing in again." />
            </Field>
            <div className="flex gap-2 sm:col-span-3">
              <Button type="submit" disabled={suspend.isPending}>
                {suspend.isPending ? "Applying…" : "Apply"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setRestricting(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {restore.errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{restore.errorMessage}</Alert>
        </div>
      ) : null}

      {data.pending.length > 0 ? (
        <Card className="mb-5">
          <CardTitle>Invitations not yet accepted</CardTitle>
          <ul className="space-y-1 text-sm text-ink-600">
            {data.pending.map((pending) => (
              <li key={pending.id}>
                {pending.email} · {pending.role}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {can("org.members") ? (
        <Card>
          <CardTitle hint="Set a password to create the login now, or leave it blank for an invitation link.">
            Add someone
          </CardTitle>
          <form
            className="grid gap-4 sm:grid-cols-4"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              invite.mutate({
                email: String(form.get("email")),
                name: String(form.get("name") || "") || undefined,
                role: String(form.get("role")),
                password: String(form.get("password") || "") || undefined,
              });
            }}
          >
            <Field label="Email" htmlFor="email" required>
              <Input id="email" name="email" type="email" required placeholder="colleague@agency.com" />
            </Field>
            <Field label="Name" htmlFor="name">
              <Input id="name" name="name" placeholder="Tunde Bakare" />
            </Field>
            <Field label="Role" htmlFor="role">
              <Select id="role" name="role" defaultValue="agent">
                {ROLES.filter((role) => canAssignRole(data.myRole, role.value)).map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Password" htmlFor="password" hint="Optional.">
              <PasswordInput id="password" minLength={8} />
            </Field>
            <div className="sm:col-span-4">
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Adding…" : "Add to team"}
              </Button>
            </div>
          </form>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {ROLES.map((role) => (
              <p key={role.value} className="text-xs text-ink-500">
                <span className="font-medium text-ink-700">{role.label}</span> — {role.description}
              </p>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
